// src/common/utils/text-utils.js
/**
 * Extract the claim text from inline citation context.
 * The claim is limited to:
 * - The same sentence as the inline citation, OR
 * - The preceding sentence if the citation is its own sentence
 * 
 * @param {string} context - The full context text (e.g., paragraph)
 * @param {string} citationText - The citation text to find in the context
 * @param {Array} allCitations - Optional array of all citations to remove from context (excluding the target citation)
 * @returns {string} The extracted claim text
 */
export function extractClaimFromInlineCitation(context, citationText, allCitations = null) {
  if (!context || !citationText) {
    return context || '';
  }

  // Clean up the context - remove LaTeX math expressions and footnote markers
  let cleanedContext = context
    // Remove LaTeX math expressions
    .replace(/\$\$[^$]+\$\$/g, '[MATH]')
    .replace(/\$[^$]+\$/g, '[MATH]')
    // Remove footnote markers like { }^{6,7} or ^{6,7}
    .replace(/\{\s*\}[^{]*/g, '')
    .replace(/\^{[^}]+}/g, '')
    // Remove superscript markers
    .replace(/<sup>[^<]+<\/sup>/g, '')
    .replace(/\^{[^}]+}/g, '');

  // Remove other citations from the context before processing
  // This prevents previous citations from being included in the claim
  if (allCitations && Array.isArray(allCitations)) {
    // Normalize the target citation for comparison
    const normalizedTargetCitation = citationText.replace(/\s+/g, ' ').trim();
    
    // Collect all other citation texts (excluding the target citation)
    const otherCitations = [];
    for (const citation of allCitations) {
      const otherCitationText = citation?.source?.raw || '';
      if (otherCitationText) {
        // Compare normalized versions to avoid removing the target citation
        const normalizedOther = otherCitationText.replace(/\s+/g, ' ').trim();
        if (normalizedOther !== normalizedTargetCitation) {
          otherCitations.push(otherCitationText);
        }
      }
    }
    
    // Sort by length (longest first) to avoid partial matches
    otherCitations.sort((a, b) => b.length - a.length);
    
    // Remove each other citation from the context
    for (const otherCitationText of otherCitations) {
      if (!otherCitationText) continue;
      
      // Escape special regex characters
      const escaped = otherCitationText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Create regex that matches the citation text (allowing for whitespace variations)
      const regex = new RegExp(escaped.replace(/\s+/g, '\\s+'), 'g');
      cleanedContext = cleanedContext.replace(regex, '');
    }
    
    // Clean up extra whitespace that might have been left
    cleanedContext = cleanedContext.replace(/\s+/g, ' ').trim();
  }

  // Find the position of the citation in the cleaned context
  // Try to find the citation text, accounting for potential formatting differences
  let citationIndex = cleanedContext.indexOf(citationText);
  
  // If not found, try to find a simplified version (remove extra spaces, punctuation)
  if (citationIndex === -1) {
    const simplifiedCitation = citationText.replace(/\s+/g, ' ').trim();
    const simplifiedContext = cleanedContext.replace(/\s+/g, ' ');
    citationIndex = simplifiedContext.indexOf(simplifiedCitation);
    if (citationIndex !== -1) {
      // Adjust for the simplified search
      cleanedContext = simplifiedContext;
    }
  }
  
  if (citationIndex === -1) {
    // Citation not found, return a cleaned version of the context
    // But limit to first sentence to avoid including too much
    const firstSentence = cleanedContext.split(/[.!?]\s+/)[0];
    return firstSentence || cleanedContext;
  }

  // Helper function to check if a period is part of an abbreviation (like case names)
  function isAbbreviationPeriod(text, pos) {
    if (pos < 0 || pos >= text.length || text[pos] !== '.') {
      return false;
    }
    
    // Common abbreviations that end with a period
    const abbreviations = new Set([
      'v.', 'vs.', 'etc.', 'e.g.', 'i.e.', 'cf.', 'ibid.', 'id.', 'op.', 'cit.', 'loc.',
      'mr.', 'mrs.', 'ms.', 'dr.', 'prof.', 'inc.', 'ltd.', 'co.', 'corp.', 'llc.',
      'u.s.', 's.c.', 'c.a.', 'f.c.', 'f.d.', 'd.c.', 'n.y.', 'cal.', 'tex.', 'fla.'
    ]);
    
    // Look backwards to find the start of the word
    let start = pos;
    while (start > 0 && (text[start - 1].match(/[\w.']/))) {
      start--;
    }
    
    // Get the word/abbreviation before the period
    const abbrevText = text.substring(start, pos).toLowerCase().trim();
    
    // Check if it's a known abbreviation
    if (abbreviations.has(abbrevText)) {
      return true;
    }
    
    // Check if it's a single letter followed by period (like "v.", "S.", "C.")
    if (abbrevText.length === 1 && /[a-z]/i.test(abbrevText)) {
      return true;
    }
    
    // Check if it's a pattern like "S.C." or "U.S." (multiple single letters)
    if (/^[a-z]\.([a-z]\.)*$/i.test(abbrevText)) {
      return true;
    }
    
    // Check if it's followed by a lowercase letter (likely not sentence end)
    if (pos + 1 < text.length && text[pos + 1].match(/[a-z]/)) {
      return true;
    }
    
    return false;
  }

  // Split text into sentences
  // Improved sentence splitting that handles case names and abbreviations
  const sentences = [];
  let currentSentence = '';
  let inParentheses = 0;
  let inBrackets = 0;
  
  for (let i = 0; i < cleanedContext.length; i++) {
    const char = cleanedContext[i];
    currentSentence += char;
    
    // Track parentheses and brackets to avoid splitting inside them
    if (char === '(') inParentheses++;
    if (char === ')') inParentheses--;
    if (char === '[') inBrackets++;
    if (char === ']') inBrackets--;
    
    // Check for sentence endings (only if not inside parentheses/brackets)
    if ((char === '.' || char === '!' || char === '?') && inParentheses === 0 && inBrackets === 0) {
      // Check if this is really a sentence end (followed by space or end of string)
      const nextChar = i + 1 < cleanedContext.length ? cleanedContext[i + 1] : '';
      if (nextChar === ' ' || nextChar === '\n' || nextChar === '' || nextChar === '\t') {
        // Check if it's not an abbreviation (like "v." in case names)
        if (char === '.' && isAbbreviationPeriod(cleanedContext, i)) {
          continue; // Skip abbreviation periods
        }
        
        // Check if followed by capital letter (sentence boundary)
        const charAfterWhitespace = i + 2 < cleanedContext.length ? cleanedContext[i + 2] : '';
        if (charAfterWhitespace && charAfterWhitespace.match(/[A-Z"']/)) {
          sentences.push({
            text: currentSentence.trim(),
            start: i - currentSentence.length + 1,
            end: i + 1
          });
          currentSentence = '';
        }
      }
    }
  }
  
  // Add the last sentence if any
  if (currentSentence.trim()) {
    sentences.push({
      text: currentSentence.trim(),
      start: cleanedContext.length - currentSentence.length,
      end: cleanedContext.length
    });
  }

  // Find which sentence contains the citation
  let citationSentenceIndex = -1;
  for (let i = 0; i < sentences.length; i++) {
    const sentence = sentences[i];
    if (citationIndex >= sentence.start && citationIndex < sentence.end) {
      citationSentenceIndex = i;
      break;
    }
  }

  if (citationSentenceIndex === -1) {
    // Couldn't find the sentence, return a cleaned version limited to first sentence
    const firstSentence = cleanedContext.split(/[.!?]\s+/)[0];
    return firstSentence || cleanedContext;
  }

  const citationSentence = sentences[citationSentenceIndex];
  const citationStartInSentence = citationIndex - citationSentence.start;
  const citationEndInSentence = citationStartInSentence + citationText.length;
  
  // Check if the citation is its own sentence (or mostly its own sentence)
  // A citation is considered "its own sentence" if:
  // 1. It starts near the beginning of the sentence (within first 10 chars), OR
  // 2. It takes up most of the sentence (more than 70% of the sentence length)
  const sentenceText = citationSentence.text;
  const isCitationOwnSentence = 
    citationStartInSentence < 10 || 
    (citationText.length / sentenceText.length) > 0.7;

  if (isCitationOwnSentence && citationSentenceIndex > 0) {
    // Citation is its own sentence, use the preceding sentence
    // Make sure the preceding sentence is not empty (after removing citations)
    const precedingSentence = sentences[citationSentenceIndex - 1].text.trim();
    if (precedingSentence) {
      return precedingSentence;
    }
    // If preceding sentence is empty, fall back to the citation sentence
    return sentenceText;
  } else {
    // Use the sentence containing the citation
    return sentenceText;
  }
}

